import { createAsyncThunk } from '@reduxjs/toolkit'
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import type { Entity, Persisted } from '@tunasong/schemas'
import { pickFields } from '../../api/conditional-save.js'
import { entitiesApi } from '../../api/entities.js'
import type { RootState } from '../../configure-store.js'
import type { TunaThunkDispatch } from '../../hooks/thunk-dispatch.hook.js'
import { entitiesSlice } from './entities-slice.js'
import type { EntityConflict } from './entities-slice.js'

/**
 * Resolving a held conflict (GH #1912 option B, docs/ux/2026-10-24-conflict-notice-ux.md, Behaviour). The conflict is
 * recorded by `updateEntity` when a structured field changed on both sides; see `conditional-save.ts`.
 */

/** Write my edit of the held fields without If-Match: mine wins, as last-write-wins did before the notice */
const saveMine = (dispatch: TunaThunkDispatch, id: string, mine: Partial<Entity>) =>
  dispatch(entitiesApi.endpoints.updateEntity.initiate({ id, partialEntity: mine, eTag: null })).unwrap()

/** The held fields a banner resolves: the ones it names that are held, or all of them when it names none */
const scopeOf = (held: EntityConflict, fields?: readonly string[]) =>
  fields ? held.fields.filter(field => fields.includes(field)) : held.fields

/**
 * "Keep my changes": resend my latest edit of the held fields, unconditionally, and release them. A banner passes the
 * fields it shows, and only those are resolved: another panel's held field stays held. An edit made while the save
 * was in flight joined the held edit, so it is sent too before the fields are released. A failure rejects and
 * leaves the conflict for "Try again".
 */
export const keepHeldEdit = createAsyncThunk<
  Persisted<Entity> | undefined,
  { id: string; fields?: readonly string[] },
  { state: RootState; dispatch: TunaThunkDispatch }
>('entities/keepHeldEdit', async ({ id, fields }, { dispatch, getState }) => {
  const mineIn = () => {
    const held = getState().entities.conflicts[id]
    return held ? pickFields(held.mine, scopeOf(held, fields)) : undefined
  }
  let saved: Persisted<Entity> | undefined
  let mine = mineIn()
  while (mine && Object.keys(mine).length > 0) {
    saved = await saveMine(dispatch, id, mine)
    const latest = mineIn()
    if (JSON.stringify(latest) === JSON.stringify(mine)) {
      dispatch(entitiesSlice.actions.releaseHeldFields({ id, fields: Object.keys(mine) }))
      break
    }
    mine = latest
  }
  return saved
})

/**
 * "Use their version": drop my held edit of the fields and release them. Nothing is sent: the server already holds
 * theirs. A banner passes the fields it shows, and only those are resolved. Returns both versions of those fields,
 * so the caller can show theirs and offer Undo (which is `undoTakeTheirVersion`).
 */
export const takeTheirVersion =
  ({
    id,
    fields,
  }: {
    id: string
    fields?: readonly string[]
  }): ThunkAction<{ mine: Partial<Entity>; theirs: Partial<Entity> } | undefined, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const held = getState().entities.conflicts[id]
    if (!held) {
      return undefined
    }
    const scope = scopeOf(held, fields)
    if (scope.length === 0) {
      return undefined
    }
    dispatch(entitiesSlice.actions.releaseHeldFields({ id, fields: scope }))
    return { mine: pickFields(held.mine, scope), theirs: pickFields(held.theirs, scope) }
  }

/** Undo after "Use their version": save my edit after all, the same as Keep */
export const undoTakeTheirVersion = createAsyncThunk<
  Persisted<Entity>,
  { id: string; mine: Partial<Entity> },
  { state: RootState; dispatch: TunaThunkDispatch }
>('entities/undoTakeTheirVersion', async ({ id, mine }, { dispatch }) => saveMine(dispatch, id, mine))

/**
 * The page is being hidden or closed with conflicts still held: save mine, unconditionally, so the edit is not lost
 * (spec, Behaviour). The write is a keepalive request, as every entity update is when its body fits.
 */
export const resendHeldConflicts = createAsyncThunk<void, void, { state: RootState; dispatch: TunaThunkDispatch }>(
  'entities/resendHeldConflicts',
  async (_, { dispatch, getState }) => {
    const { conflicts } = getState().entities
    await Promise.all(
      Object.entries(conflicts).map(async ([id, { mine }]) => {
        await saveMine(dispatch, id, mine)
        dispatch(entitiesSlice.actions.clearConflict({ id }))
      })
    )
  }
)
